/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { getAuth } from "firebase/auth";
import { logger } from "@/utils/logger";
import { isRegionMovedError, regionFromError, triggerRegionMoved } from "@/lib/region-moved";
import { explainSchoolMoved } from "@/lib/school-moved";
import {
  getDashboardApiUrl,
  getDefaultDashboardApiUrl,
  registerRegionChangeListener,
} from "@/lib/runtime-url-resolver";

const api = axios.create({
  baseURL: getDashboardApiUrl(),
  //timeout: 10000,
  timeout: 120000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Keep the shared instance's base URL in sync when the active region changes
// (Directory Service resolution at login, or the internal-admin region toggle).
// axios reads defaults.baseURL per request, so this takes effect immediately.
registerRegionChangeListener(() => {
  api.defaults.baseURL = getDashboardApiUrl();
});

/**
 * One-off GET against the DEFAULT (legacy/EU) dashboard-api, ignoring any region
 * currently applied to the shared instance.
 *
 * Used at login to ask "does this admin also exist on the default cluster?" before
 * moving an internal admin off the region the Directory resolved them to. Returns
 * null on any failure (404, network, timeout) so the caller can treat "unknown" as
 * "stay where you are" rather than stranding someone on a cluster that does not
 * know them.
 */
export async function getFromDefaultApi<T = any>(path: string): Promise<T | null> {
  try {
    const token = await getAuth().currentUser?.getIdToken();
    if (!token) return null;
    const response = await axios.get(`${getDefaultDashboardApiUrl()}${path}`, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      timeout: 15000,
    });
    return response.status === 200 ? (response.data as T) : null;
  } catch {
    return null;
  }
}

export function isEmptyResponseData(data: unknown): boolean {
  if (data === null || data === undefined) return true;
  if (Array.isArray(data)) return data.length === 0;
  if (typeof data === "object") {
    const obj = data as Record<string, unknown>;
    if (Object.keys(obj).length === 0) return true;
    // common API envelope: { data: [] } / { data: null }
    if ("data" in obj) {
      const inner = obj.data;
      if (inner === null || inner === undefined) return true;
      if (Array.isArray(inner) && inner.length === 0) return true;
    }
  }
  return false;
}

interface RequestMetadata {
  startTime: number;
  requestId: string;
}

type ConfigWithMeta = InternalAxiosRequestConfig & { metadata?: RequestMetadata };

api.interceptors.request.use(
  async (config: ConfigWithMeta) => {
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;

    config.metadata = {
      startTime: performance.now(),
      requestId: (() => {
        const c = crypto as unknown as { randomUUID?: () => string };
        return typeof c?.randomUUID === "function"
          ? c.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      })(),
    };
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response: AxiosResponse) => {
    try {
      const config = response.config as ConfigWithMeta;
      const meta = config.metadata;
      const durationMs = meta ? Math.round(performance.now() - meta.startTime) : null;
      if (isEmptyResponseData(response.data)) {
        logger.info("api.response", {
          endpoint: config.url,
          method: (config.method || "").toUpperCase(),
          status: response.status,
          durationMs,
          outcome: "empty",
          requestId: meta?.requestId,
        });
      }
    } catch {
      // swallow - logging must never break the app
    }
    return response;
  },
  (error: AxiosError) => {
    try {
      const config = (error.config || {}) as ConfigWithMeta;
      const meta = config.metadata;
      const status = error.response?.status;

      // The admin behind this session has been moved to another region's
      // cluster. Every request from this tab will fail the same way until they
      // log in again, so end the session with an explanation (region-moved.ts).
      // Logged below like any other failure, then rejected as usual.
      if (isRegionMovedError(error)) {
        triggerRegionMoved(regionFromError(error));
      }
      // A save into a school that now lives in the other region.
      explainSchoolMoved(error);

      // 404s are used as "no data found" throughout the API — ignore them entirely.
      if (status === 404) return Promise.reject(error);

      logger.error("api.failed", {
        endpoint: config.url,
        method: (config.method || "").toUpperCase(),
        status: status ?? null,
        message: error.message,
        code: error.code ?? null,
        durationMs: meta ? Math.round(performance.now() - meta.startTime) : null,
        requestId: meta?.requestId,
      });
    } catch {
      // swallow
    }
    return Promise.reject(error);
  }
);

export default api;
